import { Routes } from '@angular/router';
import { registerDateSortDescFirst } from './core/date-sort.setup';
import { tokenGuard } from './core/token.guard';

function gridPage<T>(load: () => Promise<T>): () => Promise<T> {
    return async () => {
        const [, component] = await Promise.all([registerDateSortDescFirst(), load()]);
        return component;
    };
}

export const routes: Routes = [
    {
        path: 'missing-token',
        loadComponent: () =>
            import('./features/auth/missing-token.component').then((m) => m.MissingTokenComponent),
    },
    {
        path: '',
        canActivate: [tokenGuard],
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./features/home/home.component').then((m) => m.HomeComponent),
            },
            { path: 'Home/Index', redirectTo: '' },

            {
                path: 'Report',
                loadComponent: gridPage(() =>
                    import('./features/reports/reports-list.component').then(
                        (m) => m.ReportsListComponent,
                    ),
                ),
            },
            { path: 'Report/Index', redirectTo: 'Report' },
            {
                path: 'Report/CreateNew',
                loadComponent: gridPage(() =>
                    import('./features/reports/reports-list.component').then(
                        (m) => m.ReportsListComponent,
                    ),
                ),
                data: { createNew: true },
            },
            {
                path: 'Report/ConvertWord',
                loadComponent: gridPage(() =>
                    import('./features/reports/convert-word.component').then(
                        (m) => m.ConvertWordComponent,
                    ),
                ),
            },

            {
                path: 'DataSource',
                loadComponent: gridPage(() =>
                    import('./features/data-sources/data-sources-grid.component').then(
                        (m) => m.DataSourcesGridComponent,
                    ),
                ),
            },
            { path: 'DataSource/Index', redirectTo: 'DataSource' },
            {
                path: 'DataSource/CreateNew',
                loadComponent: gridPage(() =>
                    import('./features/data-sources/data-sources-grid.component').then(
                        (m) => m.DataSourcesGridComponent,
                    ),
                ),
                data: { autoCreate: true },
            },

            {
                path: 'Dashboards',
                loadComponent: gridPage(() =>
                    import('./features/dashboards/dashboards-list.component').then(
                        (m) => m.DashboardsListComponent,
                    ),
                ),
            },
            { path: 'Dashboards/Index', redirectTo: 'Dashboards' },
            {
                path: 'Dashboards/CreateNew',
                loadComponent: gridPage(() =>
                    import('./features/dashboards/dashboards-list.component').then(
                        (m) => m.DashboardsListComponent,
                    ),
                ),
                data: { openCreate: true },
            },
            {
                path: 'Dashboards/EditDashboard',
                loadComponent: () =>
                    import('./features/dashboards/dashboard-designer.component').then(
                        (m) => m.DashboardDesignerComponent,
                    ),
            },
            {
                path: 'Dashboards/AdminDashboard',
                loadComponent: () =>
                    import('./features/dashboards/specialty-dashboard.component').then(
                        (m) => m.SpecialtyDashboardComponent,
                    ),
            },
            {
                path: 'Dashboards/ClaimsDashboard',
                loadComponent: () =>
                    import('./features/dashboards/specialty-dashboard.component').then(
                        (m) => m.SpecialtyDashboardComponent,
                    ),
            },
            {
                path: 'Dashboards/JetFileDashboard',
                loadComponent: () =>
                    import('./features/dashboards/dashboard-designer.component').then(
                        (m) => m.DashboardDesignerComponent,
                    ),
            },
            {
                path: 'Dashboards/GlapDashboard',
                loadComponent: () =>
                    import('./features/dashboards/specialty-dashboard.component').then(
                        (m) => m.SpecialtyDashboardComponent,
                    ),
            },

            {
                path: 'HeaderAndFooter',
                loadComponent: gridPage(() =>
                    import('./features/header-footer/header-footer-page.component').then(
                        (m) => m.HeaderFooterPageComponent,
                    ),
                ),
            },
            { path: 'HeaderAndFooter/Index', redirectTo: 'HeaderAndFooter' },

            {
                path: 'Watermark',
                loadComponent: gridPage(() =>
                    import('./features/watermarks/watermark-list.component').then(
                        (m) => m.WatermarkListComponent,
                    ),
                ),
            },
            { path: 'Watermark/Index', redirectTo: 'Watermark' },

            {
                path: 'Log',
                loadComponent: gridPage(() =>
                    import('./features/log/log-viewer.component').then((m) => m.LogViewerComponent),
                ),
            },
            { path: 'Log/Index', redirectTo: 'Log' },

            {
                path: 'Statistics',
                loadComponent: () =>
                    import('./features/statistics/statistics.component').then(
                        (m) => m.StatisticsComponent,
                    ),
            },

            {
                path: 'ReportStore',
                loadComponent: gridPage(() =>
                    import('./features/global-store/global-store-list.component').then(
                        (m) => m.ReportStoreComponent,
                    ),
                ),
            },
            { path: 'ReportStore/Index', redirectTo: 'ReportStore' },
            {
                path: 'DashboardsStore',
                loadComponent: gridPage(() =>
                    import('./features/global-store/global-store-list.component').then(
                        (m) => m.DashboardsStoreComponent,
                    ),
                ),
            },
            { path: 'DashboardsStore/Index', redirectTo: 'DashboardsStore' },

            {
                path: 'Designer/Preview/:id',
                loadComponent: () =>
                    import('./features/report-viewer/report-viewer.component').then(
                        (m) => m.ReportViewerComponent,
                    ),
            },
            {
                path: 'Designer/PreviewPdf/:id',
                loadComponent: () =>
                    import('./features/report-viewer/pdf-preview.component').then(
                        (m) => m.PdfPreviewComponent,
                    ),
            },
            {
                path: 'Designer/PreviewRichAndDoc/:id',
                loadComponent: () =>
                    import('./features/report-viewer/pdf-preview.component').then(
                        (m) => m.PdfPreviewComponent,
                    ),
            },
            { path: 'Designer/Index', redirectTo: 'Report' },
            {
                path: 'DocumentManagement/PdfEdit',
                loadComponent: () =>
                    import('./features/reports/pdf-edit-redirect.component').then(
                        (m) => m.PdfEditRedirectComponent,
                    ),
            },
            { path: 'Dashboards/PermissionDenied', redirectTo: 'Dashboards' },
            {
                path: 'Designer/PreviewAsOne',
                loadComponent: () =>
                    import('./features/report-viewer/preview-as-one.component').then(
                        (m) => m.PreviewAsOneComponent,
                    ),
            },
            {
                path: 'Designer/EditXtraReport/:id',
                loadComponent: () =>
                    import('./features/report-designer/report-designer.component').then(
                        (m) => m.ReportDesignerComponent,
                    ),
            },
            { path: 'Designer/EditReport/:id', redirectTo: 'Designer/EditXtraReport/:id' },
            {
                path: 'Designer/EditRichWordReport/:id',
                loadComponent: () =>
                    import('./features/rich-editor/rich-editor.component').then(
                        (m) => m.RichEditorComponent,
                    ),
            },

            {
                path: 'Designer/EditComposition/:id',
                loadComponent: () =>
                    import('./features/composition-builder/composition-builder.component').then(
                        (m) => m.CompositionBuilderComponent,
                    ),
            },

            {
                path: 'Editor/EditTemplate/:id',
                loadComponent: () =>
                    import('./features/editor/editor.component').then((m) => m.EditorComponent),
            },
            {
                path: 'Editor/EditDocument',
                loadComponent: () =>
                    import('./features/editor/editor.component').then((m) => m.EditorComponent),
            },
            {
                path: 'Editor/EditDocument/:id',
                loadComponent: () =>
                    import('./features/editor/editor.component').then((m) => m.EditorComponent),
            },
            {
                path: 'Editor/ViewDocument',
                loadComponent: () =>
                    import('./features/editor/editor.component').then((m) => m.EditorComponent),
            },
            {
                path: 'Editor/ViewDocument/:id',
                loadComponent: () =>
                    import('./features/editor/editor.component').then((m) => m.EditorComponent),
            },
            {
                path: 'PageSettings',
                loadComponent: () =>
                    import('./features/page-settings/page-settings.component').then(
                        (m) => m.PageSettingsComponent,
                    ),
            },
            { path: 'PageSettings/Index', redirectTo: 'PageSettings' },

            {
                path: 'QueryBuilder',
                loadComponent: () =>
                    import('./features/query-builder/query-builder.component').then(
                        (m) => m.QueryBuilderComponent,
                    ),
            },
            { path: 'QueryBuilder/Index', redirectTo: 'QueryBuilder' },

            {
                path: '**',
                loadComponent: () =>
                    import('./features/auth/not-found.component').then((m) => m.NotFoundComponent),
            },
        ],
    },
];
